import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Copy, Plug, CheckCircle2, ShieldCheck, Trash2, KeyRound, Terminal } from "lucide-react";

const CodeBlock = ({ code, testId }) => (
  <div className="relative group">
    <pre className="bg-[#09090B] text-zinc-200 text-xs rounded-lg p-4 overflow-x-auto border border-zinc-800 leading-relaxed" data-testid={testId}>
      <code>{code}</code>
    </pre>
    <Button size="sm" variant="ghost" data-testid={`${testId}-copy`}
      className="absolute top-2 right-2 h-7 text-zinc-400 hover:text-white hover:bg-zinc-800 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
      onClick={() => { navigator.clipboard.writeText(code); toast.success("Copied to clipboard"); }}>
      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
    </Button>
  </div>
);

const Step = ({ n, title, children }) => (
  <li className="space-y-2">
    <p className="text-sm font-semibold">{n} · {title}</p>
    {children}
  </li>
);

export default function McpGuide() {
  const [info, setInfo] = useState(null);
  const [conns, setConns] = useState([]);
  const [token, setToken] = useState("");
  const mcpUrl = `${BACKEND_URL}/api/mcp`;

  const loadConns = () => api.get("/oauth/connections").then(({ data }) => setConns(data.connections || []));
  useEffect(() => {
    api.get("/mcp/info").then(({ data }) => setInfo(data));
    loadConns();
  }, []);

  const revoke = async (id) => {
    try {
      await api.delete(`/oauth/connections/${id}`);
      toast.success("Access revoked — that connection can no longer read your data");
      loadConns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke");
    }
  };

  const makeToken = async () => {
    try {
      const { data } = await api.post("/oauth/personal-token?days=365");
      setToken(data.token);
      loadConns();
      toast.success("Token created — copy it now, it is not shown again");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create token");
    }
  };

  const desktopConfig = `{
  "mcpServers": {
    "snd-commerce": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${mcpUrl}",
               "--header", "Authorization:Bearer YOUR_TOKEN"]
    }
  }
}`;

  return (
    <div className="max-w-3xl space-y-8" data-testid="mcp-page">
      <div className="bg-card border border-border rounded-lg p-5 md:p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center shrink-0">
            <Plug className="h-4 w-4 text-white" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading text-base md:text-lg font-semibold tracking-tight">Your MCP server is live</h2>
            <p className="text-xs text-muted-foreground">{info?.tools?.length || 29} read-only tools · sign-in protected</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-2">Connector URL (Streamable HTTP):</p>
        <CodeBlock code={mcpUrl} testId="mcp-url" />
        <div className="flex items-start gap-2 mt-4 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
          <span>Anyone adding this connector must sign in with an SND Commerce account. Every tool is read-only — nothing can be changed on Amazon.</span>
        </div>
      </div>

      <section className="space-y-5">
        <h3 className="font-heading text-base font-semibold tracking-tight">Connect claude.ai (web or mobile)</h3>
        <ol className="space-y-5">
          <Step n="1" title="Open Claude → Settings → Connectors">
            <p className="text-sm text-muted-foreground">On claude.ai click your name (bottom-left) → Settings → Connectors. Requires a Pro, Max, Team or Enterprise plan.</p>
          </Step>
          <Step n="2" title="Add custom connector">
            <p className="text-sm text-muted-foreground">Click <strong>Add custom connector</strong>, name it <strong>SND Commerce</strong> and paste the URL above. Leave the OAuth Client ID and Secret fields empty — the server registers Claude automatically.</p>
          </Step>
          <Step n="3" title="Click Connect and sign in">
            <p className="text-sm text-muted-foreground">An SND Commerce sign-in page opens. Use your dashboard username and password — the same one you use here. Claude never sees your password.</p>
          </Step>
          <Step n="4" title="Enable the tools in a chat">
            <p className="text-sm text-muted-foreground">In any chat, open the tools menu (the <em>Search and tools</em> button) and switch on <strong>SND Commerce</strong>.</p>
          </Step>
          <Step n="5" title="Ask away">
            <p className="text-sm text-muted-foreground">Try: <em>"Using SND Commerce, which suppressed listings still have FBA stock, and what does Amazon want fixed on each?"</em></p>
          </Step>
        </ol>
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-xs text-muted-foreground">
          <strong className="text-foreground">If Claude says it couldn&apos;t register:</strong> remove the connector, reload claude.ai and add it again — Claude caches a failed attempt for a few minutes.
        </div>
      </section>

      <section className="space-y-3" data-testid="mcp-connections">
        <h3 className="font-heading text-base font-semibold tracking-tight">Connected apps</h3>
        {conns.length === 0 ? (
          <p className="text-sm text-muted-foreground">No app is connected yet.</p>
        ) : (
          <div className="space-y-2">
            {conns.map((c) => (
              <div key={c.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap"
                   data-testid={`mcp-conn-${c.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{c.client}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.kind} · added {c.created_at ? new Date(c.created_at).toLocaleString("en-IN") : "—"}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="rounded-full"
                        onClick={() => revoke(c.id)} data-testid={`mcp-revoke-${c.id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="font-heading text-base font-semibold tracking-tight">Tools Claude can call</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(info?.tools || []).map((t) => (
            <div key={t.name} className="bg-card border border-border rounded-lg p-4 flex items-start gap-3" data-testid={`mcp-tool-${t.name}`}>
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-mono text-sm font-semibold break-all">{t.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/40 border border-border rounded-lg p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h4 className="text-sm font-semibold">Claude Desktop, scripts or curl</h4>
        </div>
        <p className="text-sm text-muted-foreground">
          Tools that can&apos;t sign in need a personal token. It works for a year and can be revoked above at any time.
        </p>
        <Button size="sm" className="rounded-full" onClick={makeToken} data-testid="mcp-token-btn">
          <KeyRound className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Generate personal token
        </Button>
        {token && (
          <div className="space-y-2">
            <p className="text-xs text-amber-600 font-medium">Copy it now — it is never shown again.</p>
            <CodeBlock code={token} testId="mcp-token" />
          </div>
        )}
        <CodeBlock code={desktopConfig} testId="mcp-config" />
      </section>
    </div>
  );
}
